/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import { catchError, exhaustMap, map, mapTo, switchMap, takeUntil, tap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { AxiosError } from 'axios';
import { downloadDataAsFile, parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';
import {
    AlertTemplateRulesAction,
    AlertTemplateRulesActionType,
    createAlertTemplateRulesErrorAction,
    createAlertTemplateRulesSuccessAction,
    deleteAlertTemplateRulesErrorAction,
    deleteAlertTemplateRulesSuccessAction,
    downloadAlertTemplateRuleErrorAction,
    downloadAlertTemplateRuleSuccessAction,
    downloadSampleRulesErrorAction,
    downloadSampleRulesSuccessAction,
    editAlertTemplateRuleErrorAction,
    editAlertTemplateRuleSuccessAction,
    fetchAlertTemplateRulesErrorAction,
    fetchAlertTemplateRulesSuccessAction,
    fetchAlertTemplateRuleUniqueSuccessAction,
} from '../actions/AAlertTemplateRules';
import { alertsClient } from '../api/alerts/alertsClient';

export const getAlertTemplateRulesEpic: Epic<AlertTemplateRulesAction> = (action$, state$) => {
    return action$.pipe(
        ofType(
            AlertTemplateRulesActionType.OPEN_ALERT_TEMPLATE_RULES_PAGE,
            AlertTemplateRulesActionType.CREATE_ALERT_TEMPLATE_RULES_SUCCESS,
            AlertTemplateRulesActionType.EDIT_ALERT_TEMPLATE_RULE_SUCCESS,
            AlertTemplateRulesActionType.DELETE_ALERT_TEMPLATE_RULES_SUCCESS,
            AlertTemplateRulesActionType.APPLY_PAGING,
            AlertTemplateRulesActionType.APPLY_FILTER,
        ),

        switchMap(() => {
            const params = state$.value.alertTemplateRulesReducer.pageParams;
            const filterParams = state$.value.alertTemplateRulesReducer.filterParams;
            const { AlertTemplateName } = state$.value.routerState.route.params;

            return from(alertsClient.getTemplateRules(AlertTemplateName, { ...params, ...filterParams })).pipe(
                map((resp) => fetchAlertTemplateRulesSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        fetchAlertTemplateRulesErrorAction(
                            parseError(err, i18n.t('AlertsScreens.rules.errors.rulesListNotFound')),
                        ),
                    ),
                ),

                takeUntil(action$.pipe(ofType(AlertTemplateRulesActionType.EXIT_ALERT_TEMPLATE_RULES_PAGE))),
            );
        }),
    );
};

export const addRulesEpic: Epic<AlertTemplateRulesAction> = (action$, state$) => {
    return action$.pipe(
        ofType(AlertTemplateRulesActionType.CREATE_ALERT_TEMPLATE_RULES_START),

        switchMap((action) => {
            const { AlertTemplateName } = state$.value.routerState.route.params;
            return from(alertsClient.createTemplateRules(AlertTemplateName, action.payload)).pipe(
                map(() => createAlertTemplateRulesSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        createAlertTemplateRulesErrorAction(
                            parseError(err, i18n.t('AlertsScreens.rules.errors.addRuleError')),
                        ),
                    ),
                ),
                takeUntil(action$.pipe(ofType(AlertTemplateRulesActionType.EXIT_ALERT_TEMPLATE_RULES_PAGE))),
            );
        }),
    );
};

export const editRuleEpic: Epic<AlertTemplateRulesAction> = (action$, state$) => {
    return action$.pipe(
        ofType(AlertTemplateRulesActionType.EDIT_ALERT_TEMPLATE_RULE_START),
        switchMap((action) => {
            const { AlertTemplateName } = state$.value.routerState.route.params;

            return from(
                alertsClient.updateTemplateRules(AlertTemplateName, action.payload.id, {
                    ruleType: action.payload.ruleType,
                }),
            ).pipe(
                map(() => editAlertTemplateRuleSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        editAlertTemplateRuleErrorAction(
                            parseError(err, i18n.t('AlertsScreens.rules.errors.editRuleError')),
                        ),
                    ),
                ),
                takeUntil(action$.pipe(ofType(AlertTemplateRulesActionType.EXIT_ALERT_TEMPLATE_RULES_PAGE))),
            );
        }),
    );
};

export const deleteRulesEpic: Epic<AlertTemplateRulesAction> = (action$, state$) => {
    return action$.pipe(
        ofType(AlertTemplateRulesActionType.DELETE_ALERT_TEMPLATE_RULES_START),
        switchMap((action) => {
            const { AlertTemplateName } = state$.value.routerState.route.params;

            return from(alertsClient.deleteTemplateRules(AlertTemplateName, action.payload)).pipe(
                map(() => deleteAlertTemplateRulesSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        deleteAlertTemplateRulesErrorAction(
                            parseError(err, i18n.t('AlertsScreens.rules.errors.deleteRulesError')),
                        ),
                    ),
                ),

                takeUntil(action$.pipe(ofType(AlertTemplateRulesActionType.EXIT_ALERT_TEMPLATE_RULES_PAGE))),
            );
        }),
    );
};

export const templateRulesDownloadEpic: Epic<AlertTemplateRulesAction> = (action$, state$) => {
    return action$.pipe(
        ofType(AlertTemplateRulesActionType.DOWNLOAD_TEMPLATE_RULES_START),
        exhaustMap(() => {
            const filterParams = state$.value.alertTemplateRulesReducer.filterParams;
            const { AlertTemplateName } = state$.value.routerState.route.params;
            const fileName = `alert_type_${AlertTemplateName}_rules.xlsx`;

            return from(alertsClient.downloadTemplateRulesAsExcel(AlertTemplateName, filterParams)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),

                mapTo(downloadAlertTemplateRuleSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadAlertTemplateRuleErrorAction(
                            parseError(err, i18n.t('AlertsScreens.rules.errors.failedDownloadRulesList', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const sampleRulesDownloadEpic: Epic<AlertTemplateRulesAction> = (action$, state$) => {
    return action$.pipe(
        ofType(AlertTemplateRulesActionType.DOWNLOAD_SAMPLE_RULES_START),
        exhaustMap(() => {
            const fileName = 'rules_sample.xlsx';
            const { AlertEntityType } = state$.value.routerState.route.params;

            return from(alertsClient.downloadRulesSampleAsExcel(AlertEntityType)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),

                mapTo(downloadSampleRulesSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadSampleRulesErrorAction(
                            parseError(
                                err,
                                i18n.t('AlertsScreens.rules.errors.failedDownloadRuleTemplate', { fileName }),
                            ),
                        ),
                    ),
                ),
            );
        }),
    );
};

export const getAlertTemplatesRuleIdentifiersEpic: Epic<AlertTemplateRulesAction> = (action$) => {
    return action$.pipe(
        ofType(AlertTemplateRulesActionType.FETCH_ALERT_TEMPLATE_RULE_UNIQUE_IDENTIFIERS_START),
        switchMap((action) => {
            return from(alertsClient.searchTaxNumbersByType(action.payload)).pipe(
                map((resp) => fetchAlertTemplateRuleUniqueSuccessAction(resp)),

                catchError((err: AxiosError) =>
                    of(
                        fetchAlertTemplateRulesErrorAction(
                            parseError(err, i18n.t('AlertsScreens.rules.errors.failedLoadTaxNumbersList')),
                        ),
                    ),
                ),
                takeUntil(
                    action$.pipe(
                        ofType(AlertTemplateRulesActionType.FETCH_ALERT_TEMPLATE_RULE_UNIQUE_IDENTIFIERS_ERROR),
                    ),
                ),
            );
        }),
    );
};
